import { containerQueries } from "@/features/containers/api/containers-keys";
import * as Sentry from "@sentry/react";
import type { ChatMessageWithAuthorView } from "@shared/types/supabase.types";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { QueryClient } from "@tanstack/react-query";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";
import type { ChatMessageWithAuthor, ChatMessageWithAuthorInsert } from "../chat.types";
import * as chatApi from "./chat-api";
import { chatQueries } from "./chat-keys";

function toMessageTimestamp(message: { created_at?: string | null }): number {
  const value = message.created_at;
  if (!value) return 0;

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

/**
 * Insert (or replace) a server-persisted message in the room cache, keeping
 * chronological order, and refresh the room's last-message preview. Used to
 * reconcile AI replies persisted by the orchestrator (CR-37), where no client
 * mutation ran.
 */
export function upsertMessageInRoomCache(
  queryClient: QueryClient,
  message: ChatMessageWithAuthorView,
): void {
  const room = message.room;
  if (!room) return;

  void queryClient.invalidateQueries({
    queryKey: chatQueries.lastMessagesForRooms([room]).queryKey,
  });

  queryClient.setQueryData<ChatMessageWithAuthorView[]>(
    chatQueries.byRoom(room).queryKey,
    (oldMessages = []) => {
      const deduped = oldMessages.filter((existing) => existing.id !== message.id);
      return [...deduped, message].sort((a, b) => toMessageTimestamp(a) - toMessageTimestamp(b));
    },
  );
}

/**
 * Hook to fetch messages for a specific room
 * Uses TanStack Query with type-safe query key factory
 */
export function useMessagesQuery(roomName: string, enabled = true) {
  return useQuery({
    ...chatQueries.byRoom(roomName),
    enabled: enabled && !!roomName,
    staleTime: 30 * 1000, // 30 seconds - messages change frequently
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to set up real-time message subscription
 * Automatically updates cache when messages are added/updated/deleted
 */
export function useMessagesSubscription(roomName: string, enabled = true) {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: chatQueries.subscription(roomName),
    queryFn: ({ signal }): RealtimeChannel | null => {
      if (!enabled || !roomName) return null;

      const channel = chatApi.createMessageSubscription(roomName, () => {
        // Invalidate and refetch messages for this room
        void queryClient.invalidateQueries({
          queryKey: chatQueries.byRoom(roomName).queryKey,
        });
      });

      // Cleanup: when query is cancelled, disabled, unmounted or room name changes
      signal.addEventListener("abort", () => {
        void chatApi.removeMessageSubscription(channel);
      });

      return channel;
    },
    enabled: enabled && !!roomName,
    staleTime: Infinity, // Never stale - it's managing a subscription
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/** Safety net while a turn is open, in case a realtime event was missed. */
const OPEN_AI_TURN_REFETCH_MS = 15_000;

type UseAiChatTurnsOptions = {
  /**
   * CR-199: the AI chat's parent (the project). When set, a turn reaching a
   * terminal status also refreshes the room and the parent's AI-chat list: the
   * server names a new chat BEFORE it marks the first turn completed, and
   * `containers` is not in the realtime publication, so that status UPDATE is
   * the only live signal that a name may have changed.
   */
  refreshParentOnTurnEnd?: string;
};

/**
 * CR-141: the room's open durable AI turns (queued, running, failed), kept
 * fresh by a realtime subscription on ai_chat_turns. This is what shows a
 * "thinking" bubble for a turn nobody is streaming — after a reload, a
 * navigation, or for the second persona of a message — and a failure card
 * for a turn the server gave up on.
 */
export function useAiChatTurns(
  roomId: string,
  enabled = true,
  { refreshParentOnTurnEnd }: UseAiChatTurnsOptions = {},
) {
  const queryClient = useQueryClient();
  // The callback closure belongs to the cached per-room subscription query and
  // outlives this render; a ref lets the option change within an instance
  // (room type still loading → message_room). A room's parent never changes,
  // so a remount reusing an older closure is safe.
  const parentContainerIdRef = useRef<string | null>(null);
  useEffect(() => {
    parentContainerIdRef.current = refreshParentOnTurnEnd ?? null;
  }, [refreshParentOnTurnEnd]);

  useQuery({
    queryKey: chatQueries.aiTurnSubscription(roomId),
    queryFn: ({ signal }): RealtimeChannel | null => {
      const channel = chatApi.createAiTurnSubscription(roomId, (change) => {
        void queryClient.invalidateQueries({ queryKey: chatQueries.aiTurns(roomId).queryKey });
        const parentContainerId = parentContainerIdRef.current;
        if (!parentContainerId || !chatApi.isTerminalAiTurnChange(change)) return;
        void queryClient.invalidateQueries({ queryKey: containerQueries.byId(roomId).queryKey });
        void queryClient.invalidateQueries({
          queryKey: chatQueries.roomsByParent(parentContainerId).queryKey,
        });
      });
      signal.addEventListener("abort", () => {
        void chatApi.removeMessageSubscription(channel);
      });
      return channel;
    },
    enabled: enabled && !!roomId,
    staleTime: Infinity, // Never stale - it's managing a subscription
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return useQuery({
    ...chatQueries.aiTurns(roomId),
    enabled: enabled && !!roomId,
    refetchInterval: (query) =>
      query.state.data?.some((turn) => turn.status === "queued" || turn.status === "running")
        ? OPEN_AI_TURN_REFETCH_MS
        : false,
  });
}

/**
 * Hook to create a chat room under a parent container
 */
export function useCreateChatRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      parentContainerId,
      input,
    }: {
      parentContainerId: string;
      input: Partial<chatApi.ChatRoomInsert>;
    }) => chatApi.createChatRoom(parentContainerId, input),
    onSuccess: async (_, { parentContainerId, input }) => {
      const queryKey =
        input.container_type === "message_room"
          ? chatQueries.messageRoomsByParent(parentContainerId).queryKey
          : chatQueries.roomsByParent(parentContainerId).queryKey;
      await queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * CR-97: rename a message room (an empty name = back to the derived default).
 * On success the header re-resolves from `containerQueries.byId` and the
 * sidebar / Messages list from `chatQueries.messageRoomsByParent` — there is no
 * realtime subscription on containers, so other members pick the new title up
 * on their next refetch, as they do for member changes (CR-67).
 */
export function useRenameMessageRoomMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ roomId, name }: { roomId: string; parentContainerId: string; name: string }) =>
      chatApi.renameMessageRoom(roomId, name),
    onSuccess: async (_, { roomId, parentContainerId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: containerQueries.byId(roomId).queryKey }),
        queryClient.invalidateQueries({
          queryKey: chatQueries.messageRoomsByParent(parentContainerId).queryKey,
        }),
      ]);
    },
  });
}

/**
 * Hook to insert a new message
 * Implements optimistic updates with automatic cache management
 */
export function useStoreMessageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (messageWithAuthor: ChatMessageWithAuthorInsert) => {
      const message: chatApi.MessageInsert = {
        id: messageWithAuthor.id,
        actor_id: messageWithAuthor.actor_id,
        content: messageWithAuthor.content,
        room: messageWithAuthor.room,
      };

      const storedMessageWithAuthor = await chatApi.storeMessage(message);
      return storedMessageWithAuthor;
    },
    onMutate: async (messageWithAuthor) => {
      // Cancel outgoing refetches for this room
      const key = chatQueries.byRoom(messageWithAuthor.room).queryKey;
      await queryClient.cancelQueries({ queryKey: key });

      // Snapshot previous value for rollback
      const previousMessages = queryClient.getQueryData<ChatMessageWithAuthorView[]>(key);

      // Create optimistic message
      const optimisticMessage: ChatMessageWithAuthor = {
        ...messageWithAuthor,
        created_at: messageWithAuthor.created_at ?? new Date().toISOString(),
      };
      // Optimistically update cache
      queryClient.setQueryData<ChatMessageWithAuthorView[]>(key, (oldMessages = []) => {
        // Check for duplicates
        const exists = oldMessages.some((message) => message.id === optimisticMessage.id);
        if (exists) return oldMessages;

        // Add message and maintain chronological order
        return [...oldMessages, optimisticMessage].sort(
          (a, b) => toMessageTimestamp(a) - toMessageTimestamp(b),
        );
      });

      return {
        previousMessages,
        optimisticId: optimisticMessage.id,
        room: messageWithAuthor.room,
      };
    },
    onError: (error) => {
      console.error("Failed to store message:", error);
    },
    onSuccess: (data, _, context) => {
      const room = context.room;
      if (!room) return;

      void queryClient.invalidateQueries({
        queryKey: chatQueries.lastMessagesForRooms([room]).queryKey,
      });

      const key = chatQueries.byRoom(room).queryKey;
      const optimisticId = context.optimisticId;

      // Update cache with server response
      queryClient.setQueryData<ChatMessageWithAuthorView[]>(key, (oldMessages = []) => {
        const filtered = optimisticId
          ? oldMessages.filter((message) => message.id !== optimisticId)
          : oldMessages;
        // also guard against duplicates by stored id
        const dedupedMessages = filtered.filter((message) => message.id !== data.id);
        return [...dedupedMessages, data].sort(
          (a, b) => toMessageTimestamp(a) - toMessageTimestamp(b),
        );
      });
      console.log("Message stored successfully");
    },
    onSettled: (_, __, messageWithAuthor) => {
      // Always invalidate to ensure consistency with server
      if (messageWithAuthor.room) {
        void queryClient.invalidateQueries({
          queryKey: chatQueries.byRoom(messageWithAuthor.room).queryKey,
        });
      }
    },
  });
}

export function useLastMessagesForRooms(roomIds: string[]) {
  const sortedRoomIds = useMemo(() => [...roomIds].sort(), [roomIds]);

  return useQuery({
    ...chatQueries.lastMessagesForRooms(sortedRoomIds),
    enabled: sortedRoomIds.length > 0,
    // CR-106: the room list now gates its skeleton on this query, and the key
    // carries the room ids — creating or deleting a room would otherwise blink
    // the whole list back to the skeleton while the new key loads.
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * CR-67: batch participant names for message rooms, to derive Slack-style
 * titles. Membership changes rarely, so a longer staleTime is fine; realtime
 * membership edits already invalidate the room queries.
 */
export function useMemberNamesForRooms(roomIds: string[]) {
  const sortedRoomIds = useMemo(() => [...roomIds].sort(), [roomIds]);

  return useQuery({
    ...chatQueries.memberNamesForRooms(sortedRoomIds),
    enabled: sortedRoomIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to update an existing message's content
 * Implements optimistic updates with rollback on error
 */
export function useUpdateMessageMutation() {
  const queryClient = useQueryClient();
  const { logger } = Sentry;

  return useMutation({
    mutationFn: ({
      messageId,
      content,
    }: {
      messageId: string;
      content: string;
      room: string;
    }) => chatApi.updateMessage(messageId, content),
    onMutate: async ({ messageId, content, room }) => {
      const key = chatQueries.byRoom(room).queryKey;
      await queryClient.cancelQueries({ queryKey: key });

      const previousMessages = queryClient.getQueryData<ChatMessageWithAuthorView[]>(key);

      // Optimistically update the message content in cache
      queryClient.setQueryData<ChatMessageWithAuthorView[]>(key, (old = []) =>
        old.map((msg) => (msg.id === messageId ? { ...msg, content } : msg)),
      );

      return { previousMessages, room };
    },
    onError: (error, _, context) => {
      logger.error("Failed to update message", { error: String(error) });
      Sentry.captureException(error);
      if (context?.previousMessages && context.room) {
        queryClient.setQueryData(
          chatQueries.byRoom(context.room).queryKey,
          context.previousMessages,
        );
      }
    },
    onSettled: (_, __, { room }) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueries.byRoom(room).queryKey,
      });
    },
  });
}

/**
 * Hook to delete a message
 * Implements optimistic removal with rollback on error
 */
export function useDeleteMessageMutation() {
  const queryClient = useQueryClient();
  const { logger } = Sentry;

  return useMutation({
    mutationFn: ({ messageId }: { messageId: string; room: string }) =>
      chatApi.deleteMessage(messageId),
    onMutate: async ({ messageId, room }) => {
      const key = chatQueries.byRoom(room).queryKey;
      await queryClient.cancelQueries({ queryKey: key });

      const previousMessages = queryClient.getQueryData<ChatMessageWithAuthorView[]>(key);

      // Optimistically remove the message from cache
      queryClient.setQueryData<ChatMessageWithAuthorView[]>(key, (old = []) =>
        old.filter((msg) => msg.id !== messageId),
      );

      return { previousMessages, room };
    },
    onError: (error, _, context) => {
      logger.error("Failed to delete message", { error: String(error) });
      Sentry.captureException(error);
      if (context?.previousMessages && context.room) {
        queryClient.setQueryData(
          chatQueries.byRoom(context.room).queryKey,
          context.previousMessages,
        );
      }
    },
    onSettled: (_, __, { room }) => {
      void queryClient.invalidateQueries({
        queryKey: chatQueries.byRoom(room).queryKey,
      });
    },
  });
}

export function useChatRoomsByContainerQuery(containerId: string | undefined) {
  return useQuery({
    ...chatQueries.roomsByParent(containerId!),
    enabled: !!containerId,
    staleTime: 60 * 1000,
  });
}
